import { useState } from "react";
import { CheckSquare, FileText, Check, Download } from "lucide-react";
import { CareFitAssessment } from "./CareFitAssessment";

const checklistItems = [
  "Your loved one needs assistance with daily activities",
  "You want a small, home-like environment (not a large facility)",
  "24/7 care and supervision is important",
  "Personalized, one-on-one attention matters to you",
  "You prefer consistent caregivers who know your loved one",
  "A safe, licensed facility gives you confidence",
];

export function DecisionTools() {
  const [checkedItems, setCheckedItems] = useState<Set<number>>(new Set());

  const toggleItem = (index: number) => {
    const newChecked = new Set(checkedItems);
    if (newChecked.has(index)) {
      newChecked.delete(index);
    } else {
      newChecked.add(index);
    }
    setCheckedItems(newChecked);
  };

  const checkedCount = checkedItems.size;
  const progress = (checkedCount / checklistItems.length) * 100;

  // Band color is assigned by the parent (HomePage Warm Editorial Bands).
  return (
    <section className="py-16">
      <div className="container mx-auto px-4">
        <div className="text-center mb-12">
          <span className="eyebrow text-accent-strong">Making the Right Choice</span>
          <h2 className="mt-4 display-md font-heading font-semibold tracking-[-0.025em] text-foreground">
            Tools to help you{" "}
            <span className="font-display-italic font-normal text-accent-strong">decide</span>
          </h2>
          <p className="mx-auto mt-4 max-w-2xl text-muted-foreground">
            We know this decision isn't easy. These resources are designed to help you 
            evaluate whether an adult family home is the right choice for your loved one.
          </p>
        </div>

        <div className="grid gap-6 md:grid-cols-2 lg:grid-cols-3">
          {/* Decision Checklist Card */}
          <div className="rounded-2xl border border-border/50 bg-card p-6 sm:p-8 shadow-[var(--shadow-card)]">
            <div className="flex items-start gap-4 mb-6">
              <div className="inline-flex h-12 w-12 items-center justify-center rounded-full bg-accent/10 flex-shrink-0">
                <CheckSquare className="h-6 w-6 text-accent-strong" />
              </div>
              <div>
                <h3 className="font-heading text-lg font-semibold text-foreground">
                  Is an Adult Family Home Right?
                </h3>
                <p className="mt-1 text-sm text-muted-foreground">
                  Check all that apply to your situation
                </p>
              </div>
            </div>

            {/* Progress bar */}
            <div className="mb-4">
              <div className="flex justify-between text-xs text-muted-foreground mb-1">
                <span>{checkedCount} of {checklistItems.length} checked</span>
                {checkedCount >= 4 && (
                  <span className="text-accent-strong font-medium">Good match!</span>
                )}
              </div>
              <div className="h-2 rounded-full bg-muted overflow-hidden">
                <div
                  className="h-full bg-accent transition-all duration-300"
                  style={{ width: `${progress}%` }}
                />
              </div>
            </div>

            <ul className="space-y-3">
              {checklistItems.map((item, index) => (
                <li key={index}>
                  <button
                    onClick={() => toggleItem(index)}
                    aria-pressed={checkedItems.has(index)}
                    className="flex items-start gap-3 w-full text-left group rounded-lg py-1 focus:outline-none focus-visible:ring-2 focus-visible:ring-accent"
                  >
                    <div
                      className={`flex-shrink-0 h-5 w-5 rounded border-2 transition-all duration-200 flex items-center justify-center ${
                        checkedItems.has(index)
                          ? "bg-accent border-accent"
                          : "border-border group-hover:border-accent/50"
                      }`}
                    >
                      {checkedItems.has(index) && (
                        <Check className="h-3 w-3 text-accent-foreground" />
                      )}
                    </div>
                    <span
                      className={`text-sm transition-colors ${
                        checkedItems.has(index)
                          ? "text-foreground"
                          : "text-muted-foreground"
                      }`}
                    >
                      {item}
                    </span>
                  </button>
                </li>
              ))}
            </ul>
          </div>

          {/* PDF Download Card */}
          <div className="group rounded-2xl border border-border/50 bg-card p-6 sm:p-8 shadow-[var(--shadow-card)] transition-all duration-300 hover:border-accent/30 hover:shadow-[0_0_25px_5px_hsl(var(--accent)/0.4)]">
            <div className="flex flex-col h-full">
              <div className="flex items-start gap-4 mb-6">
                <div className="inline-flex h-12 w-12 items-center justify-center rounded-full bg-primary/10 flex-shrink-0 transition-all duration-300 group-hover:bg-primary/20">
                  <FileText className="h-6 w-6 text-primary" />
                </div>
                <div>
                  <h3 className="font-heading text-lg font-semibold text-foreground">
                    What to Ask When Touring
                  </h3>
                  <p className="mt-1 text-sm text-muted-foreground">
                    Essential questions for any AFH visit
                  </p>
                </div>
              </div>

              <p className="text-sm text-muted-foreground mb-6 flex-grow">
                Download our comprehensive guide with the key questions you should ask 
                when touring any adult family home. Be prepared and confident during your visits.
              </p>

              <a
                href="/documents/AFH_Tour_Checklist_Printable.pdf"
                download="AFH_Tour_Checklist.pdf"
                className="inline-flex items-center justify-center gap-2 rounded-full bg-primary/10 px-6 py-3 text-sm font-medium text-primary transition-all hover:bg-primary/20"
              >
                <Download className="h-4 w-4" />
                Download Free Guide (PDF)
              </a>
            </div>
          </div>

          {/* Care Fit Assessment */}
          <CareFitAssessment />
        </div>
      </div>
    </section>
  );
}
